import Styles from './ExternalIntegrationCard.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import CircleCheck from 'almanac-ui/icons/16/CircleCheck'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { getIntegrationDetails } from 'components/Previews/getIntegrationDetails'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Settings, AlertTriangle } from 'blaze-ui/icons'

interface ExternalIntegrationCardProps {
  icon: React.ReactNode
  title: string
  description: string
  integrations?: ExternalIntegration[]
  buttons?: React.ReactNode
  isDisabled?: boolean
  onSettingsPress?: () => void
  isDisconnected?: boolean
  isOutdated?: boolean
}

export const ExternalIntegrationCard = ({
  icon,
  title,
  description,
  integrations = [],
  buttons,
  isDisabled = false,
  onSettingsPress,
  isDisconnected = false,
  isOutdated = false,
}: ExternalIntegrationCardProps) => {
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer

  return (
    <div
      className={cx(Styles.container, {
        [Styles.connected]: integrations.length > 0,
        [Styles.disabled]: isDisabled,
        [Styles.disconnected]: isDisconnected,
      })}
    >
      <div className={Styles.content}>
        {(onSettingsPress || isDisconnected || isOutdated) && (
          <div className={Styles.topActions}>
            {(isDisconnected || isOutdated) && (
              <div
                className={Styles.warningIcon}
                data-testid={isDisconnected ? 'integration-disconnected-warning' : 'integration-outdated-warning'}
              >
                <AlertTriangle size={20} color="var(--negative-50)" />
              </div>
            )}
            {onSettingsPress && (
              <div className={Styles.settings}>
                <IconButton variant="tertiary" onPress={onSettingsPress} icon={Settings} />
              </div>
            )}
          </div>
        )}
        {icon}
        <div className={Styles.details}>
          <Heading level={5}>{title}</Heading>
          {integrations.length < 1 && (
            <p className={cx(Styles.text, { [Styles.disabledText]: isDisabled })}>{description}</p>
          )}
          {integrations.length > 0 && (
            <div className={cx(Styles.integrations, { [Styles.virtualMarketer]: isVirtualMarketer })}>
              {isVirtualMarketer && (
                <div className={Styles.providerId}>
                  <Text variant="placeholder" lineClamp={1}>
                    ID: {integrations[0].provider_id}
                  </Text>
                </div>
              )}
              <div className={Styles.integration}>
                <CircleCheck size={16} color="var(--green-50)" />
                <Text variant="metadata" color="var(--green-50)" lineClamp={1}>
                  {getIntegrationDetails(integrations[0]).username}
                  {integrations.length > 1 && `, +${integrations.length - 1} more`}
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={Styles.actions}>{buttons}</div>
    </div>
  )
}
